import Box from '@mui/material/Box';
import { FloatingWidget } from '@openx/components-core/lib/FloatingWidget/FloatingWidget.tsx';
import type { DealApi } from '@openx/types/deal.ts';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDealContext } from '../utils/context/DealContext.tsx';
import { DealTroubleshootingAgent } from './DealTroubleshootingAgent.tsx';
import { DealTroubleshootingContent } from './DealTroubleshootingFunnel/components/DealTroubleshootingContent.tsx';
import { DealTroubleshootingStateLoading } from './DealTroubleshootingFunnel/components/DealTroubleshootingStateLoading.tsx';
import { TroubleshootingControls } from './DealTroubleshootingFunnel/components/TroubleshootingControls.tsx';
import type { DateRange } from './DealTroubleshootingFunnel/components/TroubleshootingDateFilter.tsx';
import { TroubleshootingNoDataMessage } from './DealTroubleshootingFunnel/components/TroubleshootingNoDataMessage.tsx';
import {
  DEFAULT_TIME_PERIOD,
  DSP_OPTION_ALL,
  getDateRangeForExport,
  getTroubleshootingParams,
  hasFunnelData,
  type TimePeriod,
  timePeriod,
} from './DealTroubleshootingFunnel/constants.ts';
import { DealTroubleshootingFunnel } from './DealTroubleshootingFunnel/DealTroubleshootingFunnel.tsx';
import { getTroubleshootingCsvFilename } from './DealTroubleshootingFunnel/utils/getTroubleshootingCsvFilename.ts';
import {
  convertTroubleshootingFunnelToCsv,
  convertTroubleshootingMetricsToCsv,
  downloadCsv,
} from './utils/csvExport.ts';

export interface DealTroubleshootingProps {
  dealId: string;
  dealParticipants: DealApi['deal_participants'];
}

export function DealTroubleshooting({ dealId, dealParticipants }: DealTroubleshootingProps) {
  const { t } = useTranslation();

  const { isOa, useTroubleshootingFetch } = useDealContext();
  const [selectedPeriod, setSelectedPeriod] = useState<TimePeriod>(DEFAULT_TIME_PERIOD);
  const [customDateRange, setCustomDateRange] = useState<DateRange | undefined>();
  const [selectedDspId, setSelectedDspId] = useState<string>(DSP_OPTION_ALL);
  const dashboardContainerRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    dashboardContainerRef.current = document.getElementById('dashboard-content');
  }, []);

  // Calculate troubleshooting params for charts
  const troubleshootingParams = useMemo(() => {
    return getTroubleshootingParams(selectedPeriod, selectedDspId, customDateRange);
  }, [selectedPeriod, customDateRange, selectedDspId]);

  const { troubleshoot, funnelData, isLoading, error, refetch } = useTroubleshootingFetch(
    dealId,
    troubleshootingParams
  );

  const hasChartsData = troubleshoot && troubleshoot.length > 0;
  const hasFunnelDataValue = hasFunnelData(funnelData);
  const showUnifiedNoData = error || (!hasChartsData && !hasFunnelDataValue);

  const handlePeriodChange = useCallback((period: TimePeriod, dateRange?: DateRange) => {
    setSelectedPeriod(period);
    if (period === timePeriod.Custom && dateRange) {
      setCustomDateRange(dateRange);
    } else {
      setCustomDateRange(undefined);
    }
  }, []);

  const handleDspChange = useCallback((dspId: string) => {
    setSelectedDspId(dspId);
  }, []);

  const handleExportCharts = useCallback(() => {
    if (!troubleshoot || troubleshoot.length === 0) return;
    const csvContent = convertTroubleshootingMetricsToCsv(troubleshoot, t);
    const filename = getTroubleshootingCsvFilename(dealId, 'metrics', troubleshootingParams);
    downloadCsv(csvContent, filename);
  }, [troubleshoot, troubleshootingParams, dealId, t]);

  const handleExportFunnel = useCallback(() => {
    if (!funnelData) return;
    const { startDate, endDate } = getDateRangeForExport(selectedPeriod, customDateRange);
    const csvContent = convertTroubleshootingFunnelToCsv(funnelData, startDate, endDate, t);
    const filename = getTroubleshootingCsvFilename(dealId, 'funnel', troubleshootingParams);
    downloadCsv(csvContent, filename);
  }, [funnelData, selectedPeriod, customDateRange, troubleshootingParams, dealId, t]);

  return (
    <Box sx={{ flexGrow: 1, overflow: 'auto', minHeight: 0 }}>
      <TroubleshootingControls
        dealId={dealId}
        dealParticipants={dealParticipants}
        selectedPeriod={selectedPeriod}
        customDateRange={customDateRange}
        selectedDspId={selectedDspId}
        onPeriodChange={handlePeriodChange}
        onDspChange={handleDspChange}
        onExportCharts={handleExportCharts}
        onExportFunnel={handleExportFunnel}
        lastUpdated={funnelData?.last_updated}
        isLoading={isLoading}
      />
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <DealTroubleshootingStateLoading />
        </Box>
      ) : showUnifiedNoData ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <TroubleshootingNoDataMessage onReload={error ? refetch : undefined} />
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexGrow: 1, minHeight: 0, overflow: 'hidden' }}>
          <Box sx={{ flex: 1, minWidth: 0, overflow: 'auto' }}>
            <DealTroubleshootingContent
              dealId={dealId}
              dealParticipants={dealParticipants}
              selectedPeriod={selectedPeriod}
              customDateRange={customDateRange}
              selectedDspId={selectedDspId}
              troubleshoot={troubleshoot}
              error={error}
              troubleshootingParams={troubleshootingParams}
              onRefetch={error ? refetch : undefined}
            />
          </Box>
          <Box
            sx={{
              borderLeft: theme => `1px solid ${theme.palette.divider}`,
              width: 'min(704px, 50%)',
              minWidth: 0,
              overflow: 'auto',
            }}
          >
            <DealTroubleshootingFunnel
              dealId={dealId}
              selectedPeriod={selectedPeriod}
              customDateRange={customDateRange}
              funnelData={funnelData}
              error={error}
              troubleshootingParams={troubleshootingParams}
              onRefetch={error ? refetch : undefined}
            />
          </Box>
        </Box>
      )}
      {isOa && (
        <FloatingWidget
          keepMounted={true}
          storageKey="troubleshooting-agent-launcher-position"
          data-test="troubleshooting-agent-launcher"
          containerRef={dashboardContainerRef}
          content={<DealTroubleshootingAgent dealId={dealId} />}
          label="AI"
        />
      )}
    </Box>
  );
}
